// ============================================
// @gs/ui — PageHeader
// ============================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

export interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  onRefresh?: () => void;
  refreshLoading?: boolean;
}

function formatRelativeLabel(lastMs: number, nowMs: number): string {
  const diff = Math.max(0, Math.floor((nowMs - lastMs) / 1000));
  if (diff < 10) return 'à l\'instant';
  if (diff < 60) return `il y a ${diff} s`;
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `il y a ${hrs} h`;
}

function useRelativeTime(enabled: boolean) {
  const [lastRefresh, setLastRefresh] = useState(() => Date.now());
  const [tickNow, setTickNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      setTickNow(Date.now());
    }, 30_000);
    return () => clearInterval(id);
  }, [enabled]);

  const label = useMemo(
    () => formatRelativeLabel(lastRefresh, tickNow),
    [lastRefresh, tickNow],
  );

  const reset = useCallback(() => {
    const t = Date.now();
    setLastRefresh(t);
    setTickNow(t);
  }, []);

  return { label, reset };
}

export function PageHeader({
  title,
  description,
  actions,
  onRefresh,
  refreshLoading,
}: PageHeaderProps) {
  const { label, reset } = useRelativeTime(!!onRefresh);

  function handleRefresh() {
    if (!onRefresh || refreshLoading) return;
    reset();
    onRefresh();
  }

  return (
    <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <h1 className="text-[22px] font-bold leading-tight text-slate-800 dark:text-slate-100">{title}</h1>
        {description && (
          <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">{description}</p>
        )}
        {onRefresh && (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshLoading}
            title="Cliquer pour rafraîchir"
            aria-label={`Rafraîchir — ${label}`}
            className="mt-1 flex cursor-pointer items-center gap-1.5 rounded-md px-1 py-0.5 text-[11px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:pointer-events-none disabled:opacity-50 dark:text-slate-500 dark:hover:bg-slate-700/50 dark:hover:text-slate-300"
          >
            <RefreshCw size={11} className={refreshLoading ? 'shrink-0 animate-spin' : 'shrink-0'} />
            <span className="tabular-nums">{label}</span>
          </button>
        )}
      </div>
      {actions && (
        <div className="flex w-full shrink-0 flex-wrap items-center justify-start gap-2 sm:w-auto sm:justify-end">
          {actions}
        </div>
      )}
    </div>
  );
}
